import Logger from '@js/logger';
import { NetworkError } from '@js/errors/NetworkError';

/*
 * For testing orders: make sure to turn on json-server (json-server --watch db.json).
 * Use: `apiOrderUrl(order, true)` and add an endpoint to the url from below`.
 * Available endpoints: '/merged-no-tracking', '/merged-delivered, '/merged-progress',
 * '/split-transit', '/split-delivered'; check `http://localhost:3000` for all routes
 * You may have to clear your localStorage for any saved data from previous calls.
 */

const API_URLS = {
  JWT: 'https://solarisjapan.com/tools/jwt',
  ACCOUNT: process.env.ACCOUNT_API_URL || 'https://account.solarisjapan.com',
};

/**
 * Example: https://account.solarisjapan.com/customer/123456
 *
 * @param {string} customerSid shopify customer id
 * @returns {string} api endpoint for the given customer
 */
export const apiCustomerUrl = (customerSid) => {
  return `${API_URLS.ACCOUNT}/customer/${customerSid}`;
};

/**
 * Example: https://account.solarisjapan.com/customer/123456/orders/123456
 *
 * @param {object} order
 * @returns {string} api endpoint for the given order
 */
export const apiOrderUrl = (order) => {
  return `${apiCustomerUrl(order.customer.id)}/orders/${order.id}`;
};

const fetchNewJwt = async () => {
  if (!window.SlrsIdentify.id) {
    throw new Error('No customer found');
  }

  const resp = await fetch(`${API_URLS.JWT}/customers/${window.SlrsIdentify.id}`);
  const data = await resp.json();

  return data;
};

export const getJwt = async () => {
  const now = new Date().getTime();
  let data = window.localStorage.getItem('jwt');

  if (data !== null) {
    data = JSON.parse(data);
  }

  // expire local storage after 1 hr
  if (data === null || data.createdAt + 3600000 < now) {
    window.localStorage.removeItem('jwt');

    data = await fetchNewJwt();
    window.localStorage.setItem(
      'jwt',
      JSON.stringify({
        ...data,
        createdAt: now,
      }),
    );
  }

  return data.token;
};

const setHeaders = async (headers = {}) => {
  const jwt = await getJwt();

  return {
    ...headers,
    'x-jwt-token': jwt,
  };
};

const setHeadersJson = async (headers = {}) => {
  return setHeaders({ ...headers, 'Content-Type': 'application/json' });
};

/**
 * Sends a request to abort a pending cancellation.
 *
 * @param {object} cancellationData The form data to submit
 * @returns
 */
export const deleteCancellation = async (cancellationData) => {
  try {
    const resp = await fetch(`${API_URLS.ACCOUNT}/orders/abort_cancel`, {
      method: 'POST',
      body: JSON.stringify(cancellationData),
      headers: await setHeadersJson(),
    });

    const data = await resp.json();
    return data;
  } catch (e) {
    Logger.error(e);
    return false;
  }
};

/**
 * Fetches data from the specified API endpoint.
 *
 * @param {string} url - The URL of the API endpoint.
 * @returns {Promise<object>} - A promise that resolves to the JSON response from the API.
 * @throws {Error} - HTTP error with the response status.
 */
export const fetchJson = async (url) => {
  const resp = await fetch(url, {
    headers: await setHeadersJson(),
  });

  if (!resp.ok) {
    const error = new NetworkError(`HTTP error: ${resp.status}`);
    error.info = await resp.text();
    error.status = resp.status;
    throw error;
  }

  return resp.json();
};

/** *
 * Submits an update to change the displayShipping value for the given order.
 *
 * @param {object} order The order in subject
 * @param {boolean} displayShipping The new displayShipping value
 */
export const patchShippingDisplay = async (order, displayShipping) => {
  try {
    const resp = await fetch(`${apiOrderUrl(order)}/displayShipping`, {
      method: 'PATCH',
      headers: await setHeadersJson(),
      body: JSON.stringify({
        displayShipping,
      }),
    });

    const data = await resp.json();
    return data;
  } catch (e) {
    Logger.error(e, { context: 'invoice' });
    return false;
  }
};

const shippingMethodFetch = async (method, order) => {
  try {
    const resp = await fetch(`${apiOrderUrl(order)}/request_shipping_change`, {
      method,
      headers: await setHeadersJson(),
    });

    const data = await resp.json();
    return data;
  } catch (e) {
    Logger.error(e, { context: 'request_shipping_change' });
    return false;
  }
};

/**
 * Deletes an existing request to change the shipping method for the given order.
 *
 * @param {object} order The order in subject
 */
export const deleteShippingMethodChange = async (order) => {
  return shippingMethodFetch('DELETE', order);
};

/**
 * Submits a request to change the shipping method for the given order.
 *
 * @param {object} order The order in subject
 */
export const postShippingMethodChange = async (order) => {
  return shippingMethodFetch('POST', order);
};

/**
 * Submits a request to change the address for the given order and its related parcels.
 *
 * @param {object} order The order in subject
 * @param {object} address The new address
 * @returns {Promise<object>} - A promise that resolves to the JSON response from the API.
 */
export const postAddressChangeRequest = async (order, address) => {
  try {
    const resp = await fetch(`${apiOrderUrl(order)}/request_address_change`, {
      method: 'POST',
      headers: await setHeadersJson(),
      body: JSON.stringify({
        address,
      }),
    });

    const data = await resp.json();
    return data;
  } catch (e) {
    Logger.error(e, { context: 'request_address_change' });
    return false;
  }
};

/**
 * Submits form data to cancel items from a parcel.
 *
 * @param {object} cancellationData The form data to submit
 * @returns
 */
export const postCancellation = async (cancellationData) => {
  try {
    const resp = await fetch(`${API_URLS.ACCOUNT}/orders/cancel`, {
      method: 'POST',
      body: JSON.stringify(cancellationData),
      headers: await setHeadersJson(),
    });

    const data = await resp.json();
    return data;
  } catch (e) {
    Logger.error(e);
    return false;
  }
};

/**
 * Submits an update to subscribe the customer to the newsletter and opt-in to email consent if necessary.
 *
 * @param {string} customerSid customer shopify id
 * @param {array} interests email interests
 */
export const postNewsletterSubscribe = async (email, interests) => {
  try {
    const resp = await fetch(`${API_URLS.ACCOUNT}/customer/newsletter_subscribe`, {
      method: 'POST',
      headers: await setHeadersJson(),
      body: JSON.stringify({
        email,
        interests,
      }),
    });

    const data = await resp.json();
    return data;
  } catch (e) {
    Logger.error(e, { context: 'newsletter_subscribe' });
    return false;
  }
};

/**
 * @typedef VerificationData
 * @type {object}
 * @property {string[]} links  - urls e.g. https://instagram.com
 * @property {string[]} images - base64 encoded image data
 */

/**
 * Submits the given data for verification to Lunaful (proxied through the account api).
 *
 * @param {string} customerSid customer shopify id
 * @param {VerificationData} data urls and images
 * @returns
 */
export const postVerification = async (customerSid, data) => {
  try {
    const resp = await fetch(`${apiCustomerUrl(customerSid)}/verification`, {
      method: 'POST',
      headers: await setHeadersJson(),
      body: JSON.stringify(data),
    });

    if (!resp.ok) {
      const body = await resp.text();
      throw new NetworkError(`Response [status="${resp.statusText}"],[body="${body}"]`);
    }

    return resp.json();
  } catch (e) {
    Logger.error(e, { context: 'verification', params: { customerSid } });
    return false;
  }
};

export const postItemImagesConfirm = async (order, lineItemIds) => {
  try {
    const resp = await fetch(`${apiOrderUrl(order)}/images/confirm`, {
      method: 'POST',
      headers: await setHeaders(),
      body: JSON.stringify({
        shopify_order_ids: lineItemIds,
      }),
    });

    if (!resp.ok) {
      const body = await resp.text();
      throw new NetworkError(`Response [status="${resp.statusText}"],[body="${body}"]`);
    }

    return resp.json();
  } catch (e) {
    Logger.error(e, { context: 'itemImages', params: { orderSid: order.id, lineItemIds } });
    return false;
  }
};

export default {
  fetchJson,
  getJwt,
  patchShippingDisplay,
  postAddressChangeRequest,
  postCancellation,
  postItemImagesConfirm,
  postNewsletterSubscribe,
  postVerification,
};
